import React, {useState} from 'react'
import {Button, Input} from 'react-daisyui'
import {
  useSendUserAttributeVerificationCodeMutation,
  useVerifyUserAttributeMutation
} from '../../services/cognitoApi'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {faClose, faCheckCircle} from '@fortawesome/free-solid-svg-icons'
import LoadingSpinner, {LoadingSpinnerSize} from '../common/LoadingSpinner'

interface VerifyUserAttributeProps {
  attribute: string
  attributeName: string
  attributeValue: any
  onVerifySuccess?: () => void
}

enum VerificationStep {
  SendCode,
  EnterCode,
  Success
}

export const VerifyUserAttribute = ({
  attribute,
  attributeName,
  attributeValue,
  onVerifySuccess
}: VerifyUserAttributeProps) => {
  const [verificationStep, setVerificationStep] = useState<VerificationStep>(
    VerificationStep.SendCode
  )

  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const [
    sendUserAttributeVerificationCode,
    sendUserAttributeVerificationCodeResult
  ] = useSendUserAttributeVerificationCodeMutation()

  const [
    verifyUserAttribute,
    verifyUserAttributeResult
  ] = useVerifyUserAttributeMutation()

  const handleSendCode = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    sendUserAttributeVerificationCode(attribute).then(result => {
      if ('error' in result) {
        if ('message' in result.error) {
          setErrorMessage(result.error.message)
        } else {
          setErrorMessage('Unable to send verification code at this time')
        }
      } else {
        setVerificationStep(VerificationStep.EnterCode)
      }
    })
  }

  const handleSubmitVerificationCode = (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault()
    const target = e.target as HTMLFormElement
    verifyUserAttribute({
      userAttributeKey: attribute,
      confirmationCode: (target.elements as any).verificationCode.value
    }).then(result => {
      if ('error' in result) {
        if ('message' in result.error) {
          setErrorMessage(result.error.message)
        } else {
          setErrorMessage(`Unable to send verify ${attributeName} at this time`)
        }
      } else {
        setVerificationStep(VerificationStep.Success)
        if (onVerifySuccess) {
          onVerifySuccess()
        }
      }
    })
  }

  return (
    <div className="bg-white">
      <h1 className="text-xl font-semibold" id="verify-header">
        Verify {attributeName}
      </h1>
      {errorMessage && (
        <div className="text-red-500 pt-2" role="alert" aria-live="assertive">
          {errorMessage}
        </div>
      )}

      {verificationStep === VerificationStep.EnterCode &&
        sendUserAttributeVerificationCodeResult.data && (
          <div>
            <p>
              Code sent to:{' '}
              {sendUserAttributeVerificationCodeResult.data.destination}
            </p>
            <form
              className="form-control"
              onSubmit={handleSubmitVerificationCode}
              aria-labelledby="verify-header"
            >
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="label pt-6 pb-1" htmlFor="verificationCode">
                    Enter Verification Code
                  </label>
                  <Input
                    id={`verificationCode${attribute}`}
                    name="verificationCode"
                    className="input-form"
                    aria-required="true"
                  ></Input>
                </div>
                <button
                  type="submit"
                  className="btn btn-secondary"
                  aria-label="Submit verification code"
                >
                  Submit
                </button>
                {verifyUserAttributeResult.isLoading && (
                  <div className="h-full w-full flex flex-row justify-center items-center gap-2 pt-2">
                    <LoadingSpinner size={LoadingSpinnerSize.Small} />
                  </div>
                )}
              </div>
            </form>
            <form method="dialog">
              <Button
                className="btn btn-link no-underline text-sm w-full"
                aria-label="Cancel verification"
              >
                cancel
              </Button>
            </form>
          </div>
        )}

      {verificationStep === VerificationStep.SendCode && (
        <form onSubmit={handleSendCode} aria-labelledby="verify-header">
          <label className="label pt-6 pb-1" htmlFor="attributeValue">
            <span className="label-text">{attributeName}</span>
          </label>
          <Input
            id={`attributeValue${attribute}`}
            value={attributeValue}
            disabled
            required
            className="input-form"
            aria-readonly="true"
          ></Input>
          <Button
            className="mt-6 btn-md text-lg font-semibold lowercase w-full"
            type="submit"
            color="secondary"
            aria-label={`Send verification code for ${attributeName}`}
          >
            Send Code
          </Button>
          {sendUserAttributeVerificationCodeResult.isLoading && (
            <div className="h-full w-full flex flex-row justify-center items-center gap-2 pt-2">
              <LoadingSpinner size={LoadingSpinnerSize.Small} />
            </div>
          )}
          <form method="dialog">
            <Button
              className="btn btn-link no-underline text-sm w-full"
              aria-label="Cancel verification"
            >
              cancel
            </Button>
          </form>
        </form>
      )}

      {verificationStep === VerificationStep.Success && (
        <div className="">
          <div className="absolute top-0 right-0">
            <form method="dialog">
              <button
                className="btn-close"
                aria-label="Close cancellation policy"
              >
                <FontAwesomeIcon
                  icon={faClose}
                  className="text-xl fa-thin text-center"
                />
              </button>
            </form>
          </div>
          <div className="text-green-500 flex flex-col items-center p-4">
            <FontAwesomeIcon icon={faCheckCircle} className="text-5xl p-4" />
          </div>
          <div className="text-center">
            Your {attributeName} has been successfully verified!
          </div>
        </div>
      )}
    </div>
  )
}

export default VerifyUserAttribute
